import { LandProfileDisplay } from '@cibo/ui'
import EditIcon from '@mui/icons-material/Edit'
import WarningIcon from '@mui/icons-material/WarningAmberOutlined'
import { Box, IconButton, Stack, Tooltip, Typography } from '@mui/material'
import {
  DataGridPro,
  GridCellParams,
  GridPreProcessEditCellProps,
  GridRenderCellParams,
  GridRow,
  GridRowParams,
  GridRowProps,
  GridRowSelectionModel,
  useGridApiContext,
} from '@mui/x-data-grid-pro'
import { TFunction } from 'i18next'
import { assocPath } from 'ramda'
import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'
import { LandThumbnail, LandThumbnailSkin } from '../../../components/LandThumbnail'

const nameSchema = Yup.string().required().min(1).matches(/\S+/).max(255)

const TooltipRow = (props: GridRowProps) => {
  const { t } = useTranslation('@cibo/landmanager/features/UploadFlow')

  return props.row?.alreadyExists ? (
    <Tooltip title={`${t('duplicateRow')}`}>
      <GridRow {...props} />
    </Tooltip>
  ) : (
    <GridRow {...props} />
  )
}

const NameCell = ({
  id,
  name,
  alreadyExists,
}: {
  id: number
  name: string
  alreadyExists: boolean
}) => {
  const apiRef = useGridApiContext()

  const handleClick = useCallback(
    (id: any, field: any) => {
      apiRef.current.startCellEditMode({ id, field })
    },
    [apiRef.current]
  )

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1}
      data-testid="field-name-cell"
      height="100%"
    >
      <Typography title={name}>{name}</Typography>
      {!alreadyExists && (
        <IconButton
          onClick={() => handleClick(id, 'name')}
          size="small"
          sx={{ color: 'text.disabled' }}
          data-testid="edit-name-button"
        >
          <EditIcon fontSize="small" sx={{ fontSize: 15 }} />
        </IconButton>
      )}
    </Stack>
  )
}

const DEFAULT_COLUMNS = (t: TFunction) => [
  {
    field: 'thumbnail',
    headerName: '',
    width: 80,
    resizable: false,
    renderCell: ({ row }: GridRenderCellParams) => (
      <LandThumbnail
        field={row._meta.landProfile}
        width={60}
        height={60}
        skin={LandThumbnailSkin.SMALL}
      />
    ),
  },
  {
    field: 'name',
    headerName: t('name'),
    hideable: false,
    editable: true,
    flex: 5,
    valueGetter: (_: any, row: { name: string }) => `${row.name}`,
    renderCell: ({ row: { id, name, alreadyExists } }: GridRenderCellParams) => (
      <NameCell id={id} name={name} alreadyExists={alreadyExists} />
    ),
    valueSetter: (value: any, row: any) => {
      const name = value?.toString()
      return { ...row, name }
    },
    preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
      try {
        nameSchema.validateSync(params.props.value)
        return { ...params.props }
      } catch (error) {
        return { ...params.props, error: true }
      }
    },
  },
  {
    field: 'acres',
    headerName: t('acres'),
    type: 'number' as const,
    sortable: true,
    width: 150,
    flex: 1,
    valueFormatter: (value: number | undefined) => LandProfileDisplay.acreage(value),
    valueGetter: (_: any, row: any) => {
      return row._meta.landProfile.acreage
    },
  },
  {
    field: 'warning',
    headerName: '',
    displayHeader: false,
    width: 30,
    resizable: false,
    renderCell: ({ row: { alreadyExists } }: GridRenderCellParams) => (
      <>
        {!!alreadyExists && (
          <WarningIcon color="warning" aria-label={t('alreadyExists') as string} />
        )}
      </>
    ),
  },
]

type Props = {
  fields?: any[]
  onEditsChanged(edits: any): void
  selectedFieldIds: GridRowSelectionModel
  setSelectedFields(selectedFieldIds: GridRowSelectionModel): void
  setIsEditing: Dispatch<SetStateAction<boolean>>
}

export const UploadFieldTable = ({
  selectedFieldIds,
  setSelectedFields,
  fields,
  onEditsChanged,
  setIsEditing,
}: Props) => {
  const { t } = useTranslation('@cibo/landmanager/features/UploadFlow')
  const [edits, setEdits] = useState<Record<string, Record<string, any>>>({})

  const handleCommitCell = (params: { id: string; field: string; value: string }) => {
    setIsEditing(false)
    setEdits(assocPath([params.id, params.field], params.value, edits))
    selectedFieldIds.ids.add(params.id)
    setSelectedFields({ type: 'include', ids: selectedFieldIds.ids })
  }

  useEffect(() => {
    onEditsChanged(edits)
  }, [edits])

  if (!fields) {
    return null
  }

  return (
    <Box sx={{ flex: 1, height: '300', display: 'flex' }}>
      <DataGridPro
        slots={{ row: TooltipRow }}
        slotProps={{ row: { 'data-testid': 'upload-field-table-row' } }}
        columns={DEFAULT_COLUMNS(t)}
        rows={fields}
        pageSizeOptions={[10]}
        checkboxSelection={true}
        pagination={true}
        paginationMode="client"
        rowSelectionModel={selectedFieldIds}
        onRowSelectionModelChange={setSelectedFields}
        processRowUpdate={row => {
          const params = {
            // @ts-ignore
            id: row.id,
            field: 'name',
            value: row.name,
          }
          handleCommitCell(params)
          return row
        }}
        onProcessRowUpdateError={console.error}
        onCellEditStart={() => setIsEditing(true)}
        disableRowSelectionOnClick
        isRowSelectable={({ row }: GridRowParams) => !row.alreadyExists}
        isCellEditable={({ row }: GridCellParams) => !row.alreadyExists}
      />
    </Box>
  )
}
